import { Formik } from 'formik';
import React, { useCallback, useMemo } from 'react';
import { Form } from 'react-bootstrap';

import AutoSave from '@components/AutoSave';
import MultiValueAutocompleteField from '@components/Form/MultiValueAutocompleteField';
import { useEntitiesCreate, useProfileUpdate } from '@hooks';
import { autocompleteEntities } from '@utils/backend-api/entities';
import { AutocompleteRecord } from '@utils/interfaces';

import WeekendSchema from './WeekendSchema';

interface WeekendProps {
  favoriteVacationSpots: AutocompleteRecord[];
  favoriteSportsToPlay: AutocompleteRecord[];
  favoriteSportsToWatch: AutocompleteRecord[];
  favoriteBooks: AutocompleteRecord[];
  favoriteShows: AutocompleteRecord[];
  isDisabled?: boolean;
  userId: number;
}

const Weekend = ({
  userId,
  favoriteVacationSpots,
  favoriteSportsToPlay,
  favoriteSportsToWatch,
  favoriteBooks,
  favoriteShows,
  isDisabled,
}: WeekendProps) => {
  const initialState = useMemo(
    () => ({
      favoriteVacationSpots: favoriteVacationSpots || [],
      favoriteSportsToPlay: favoriteSportsToPlay || [],
      favoriteSportsToWatch: favoriteSportsToWatch || [],
      favoriteBooks: favoriteBooks || [],
      favoriteShows: favoriteShows || [],
    }),
    [
      favoriteVacationSpots,
      favoriteSportsToPlay,
      favoriteSportsToWatch,
      favoriteBooks,
      favoriteShows,
    ]
  );
  const { mutateAsync: createEntityAsync } = useEntitiesCreate();
  const { saveProfile, saveStatus, showSave } = useProfileUpdate({ userId });

  const handleSubmit = useCallback(
    (values) => {
      const sanitizedParams = {
        vacation_spots: values.favoriteVacationSpots.map((i) => i.value),
        sports_play: values.favoriteSportsToPlay.map((i) => i.value),
        sports_watch: values.favoriteSportsToWatch.map((i) => i.value),
        books: values.favoriteBooks.map((i) => i.value),
        shows: values.favoriteShows.map((i) => i.value),
      };

      saveProfile(sanitizedParams);
    },
    [saveProfile]
  );

  return (
    <Formik initialValues={initialState} onSubmit={handleSubmit} validationSchema={WeekendSchema}>
      {(formik) => (
        <Form noValidate onSubmit={formik.handleSubmit}>
          <AutoSave debounceMs={500} />
          <div className="Weekend section-container">
            <div className="section-header mt-2">Weekend</div>
            <div className="section-detail">
              {showSave && (
                <div className={`save-label ${saveStatus === 'Saved' ? 'success' : 'failure'}`}>
                  {saveStatus}
                </div>
              )}
              <div className="section-form-container">
                <MultiValueAutocompleteField
                  isDisabled={isDisabled}
                  label="Favorite Vacation Spots"
                  name="favoriteVacationSpots"
                  fetchMethod={(value) =>
                    autocompleteEntities({
                      fragment: value,
                      entity_type: 'location',
                    })
                  }
                  createMethod={(value) =>
                    createEntityAsync({
                      name: value,
                      entity_type: 'location',
                      word_type: 'proper',
                    })
                  }
                />
                <MultiValueAutocompleteField
                  isDisabled={isDisabled}
                  label="Favorite Sports to Play"
                  name="favoriteSportsToPlay"
                  fetchMethod={(value) =>
                    autocompleteEntities({
                      fragment: value,
                      entity_type: 'sport',
                    })
                  }
                  createMethod={(value) =>
                    createEntityAsync({
                      name: value,
                      entity_type: 'sport',
                      word_type: 'proper',
                    })
                  }
                />
                <MultiValueAutocompleteField
                  isDisabled={isDisabled}
                  label="Favorite Sports to Watch"
                  name="favoriteSportsToWatch"
                  fetchMethod={(value) =>
                    autocompleteEntities({
                      fragment: value,
                      entity_type: 'sport',
                    })
                  }
                  createMethod={(value) =>
                    createEntityAsync({
                      name: value,
                      entity_type: 'sport',
                      word_type: 'proper',
                    })
                  }
                />
                <MultiValueAutocompleteField
                  isDisabled={isDisabled}
                  label="Favorite Books"
                  name="favoriteBooks"
                  fetchMethod={(value) =>
                    autocompleteEntities({
                      fragment: value,
                      entity_type: 'work_of_art',
                    })
                  }
                  createMethod={(value) =>
                    createEntityAsync({
                      name: value,
                      entity_type: 'work_of_art',
                      word_type: 'proper',
                    })
                  }
                />
                <MultiValueAutocompleteField
                  isDisabled={isDisabled}
                  label="Favorite TV Shows / Movies"
                  name="favoriteShows"
                  fetchMethod={(value) =>
                    autocompleteEntities({
                      fragment: value,
                      entity_type: 'work_of_art',
                    })
                  }
                  createMethod={(value) =>
                    createEntityAsync({
                      name: value,
                      entity_type: 'work_of_art',
                      word_type: 'proper',
                    })
                  }
                />
              </div>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default Weekend;
